/* eslint-disable react/no-unknown-property */
import { MeshProps, useLoader } from "@react-three/fiber";
import { animate } from "framer-motion";
import React, { FC, useEffect, useRef, useState } from "react";
import { Mesh, TextureLoader, Vector3 } from "three";

import PinTextureSelected from "./textures/pin-selected.png";
import PinTexture from "./textures/pin.png";

export interface MarkerProps extends Omit<MeshProps, "position"> {
  position: Vector3;
  isSelected: boolean;
  width: number;
  height: number;
}

export const Marker: FC<MarkerProps> = ({
  isSelected,
  width,
  height,
  position,
  onPointerEnter,
  onPointerLeave,
  ...rest
}) => {
  const ref = useRef<Mesh>(null);
  const [texture, textureSelected] = useLoader(TextureLoader, [PinTexture, PinTextureSelected]);
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    document.body.style.cursor = isHovered ? "pointer" : "auto";
  }, [isHovered]);

  const baseMarkerWidth = width / 5;
  const [circleWidth, setCircleWidth] = useState(baseMarkerWidth);
  useEffect(() => {
    animate(0, 50, {
      duration: 0.2,
      ease: "easeInOut",
      onUpdate: (latest) => {
        setCircleWidth((((isHovered ? 1 : 0) * latest) / 100) * baseMarkerWidth + baseMarkerWidth);
      },
    });
  }, [isHovered, baseMarkerWidth]);

  useEffect(() => {
    if (ref.current && position) {
      const lookDirection = new Vector3(position.x * 2, position.y * 2, position.z * 2);
      ref.current.lookAt(lookDirection);
    }
  }, [position]);

  return (
    <mesh
      position={position}
      onPointerEnter={(e) => {
        setIsHovered(true);
        if (onPointerEnter) onPointerEnter(e);
      }}
      onPointerLeave={(e) => {
        setIsHovered(false);
        if (onPointerLeave) onPointerLeave(e);
      }}
      {...rest}
      ref={ref}
    >
      {isSelected ? (
        <>
          <planeGeometry args={[width, height, 1, 1]} />
          <meshStandardMaterial
            alphaTest={0.5}
            map={isSelected ? textureSelected : texture}
            transparent
          />
        </>
      ) : (
        <>
          <circleGeometry args={[circleWidth, 32]} />
          <meshBasicMaterial color="#006063" />
        </>
      )}
    </mesh>
  );
};
